import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/app/environments/environment';
import { Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ReportManagementService {
  private apiBaseUrl = environment.baseApiUrl;

  constructor(private http: HttpClient) {}

  // 🔹 1. Save Report Issue
  saveReportIssue(formData: FormData): Observable<any> {
    const url = `${this.apiBaseUrl}api/ReportIssue/saveReportIssue`;
    return this.http.post(url, formData).pipe(catchError(this.handleError));
  }

  // 🔹 2. Get All Reports (supports search + pagination + filters + date)
  getAllReports(
    searchTerm: string = '',
    page: number = 1,
    pageSize: number = 10,
    modules: string[] = [],
    statuses: string[] = [],
    assignedToList: string[] = [],
    startDate?: string,   // 🗓️ added
    endDate?: string      // 🗓️ added
  ): Observable<any> {
    const url = `${this.apiBaseUrl}api/ReportIssue/GetAllReports`;

    let params = new HttpParams()
      .set('pageNumber', page)
      .set('pageSize', pageSize);

    if (searchTerm.trim() !== '') {
      params = params.set('search', searchTerm.trim());
    }

    // Add filter parameters
    if (modules.length > 0) {
      modules.forEach(module => {
        params = params.append('modules', module);
      });
    }

    if (statuses.length > 0) {
      statuses.forEach(status => {
        params = params.append('statuses', status);
      });
    }

    if (assignedToList.length > 0) {
      assignedToList.forEach(assignedTo => {
        params = params.append('assignedTo', assignedTo);
      });
    }

    // 📅 Add date filters (optional)
    if (startDate) {
      params = params.set('StartDate', startDate);
    }
    if (endDate) {
      params = params.set('EndDate', endDate);
    }

    return this.http.get(url, { params }).pipe(catchError(this.handleError));
  }

  // 🔹 3. Update Report (Status + Assigned + Deleted)
  updateReport(body: any): Observable<any> {
    const url = `${this.apiBaseUrl}api/ReportIssue/UpdateReport`;
    return this.http.put(url, body).pipe(catchError(this.handleError));
  }

  // 🔹 4. Soft Delete Report
  deleteReport(id: string, assignedBy: string): Observable<any> {
    const url = `${this.apiBaseUrl}api/ReportIssue/UpdateReport`;

    // Reusing same update API — setting isDeleted = true
    const body = {
      id: id,
      isDeleted: true,
      assignedBy: assignedBy
    };

    return this.http.put(url, body).pipe(catchError(this.handleError));
  }

  // 🔹 5. Download Attachment
  downloadAttachment(fileName: string): Observable<Blob> {
    const url = `${this.apiBaseUrl}api/ReportIssue/downloadAttachment`;
    const params = new HttpParams().set('fileName', encodeURIComponent(fileName));
    return this.http.get(url, { params, responseType: 'blob' }).pipe(catchError(this.handleError));
  }

  // 🔹 6. Get Filter Options
  getFilterOptions(): Observable<any> {
    const url = `${this.apiBaseUrl}api/ReportIssue/GetFilterOptions`;
    return this.http.get(url).pipe(catchError(this.handleError));
  }

  // 🔹 7. Export Incidents to Excel
  exportIncidentsToExcel(includeDeleted: boolean = true): Observable<Blob> {
    const url = `${this.apiBaseUrl}api/ReportIssue/ExportIncidentsExcel`;
    const params = new HttpParams().set('includeDeleted', includeDeleted.toString());
    return this.http.get(url, { params, responseType: 'blob' }).pipe(catchError(this.handleError));
  }

  // 🔹 8. Download Report Issue ZIP
  downloadReportIssue(incidentNo: string): Observable<Blob> {
    const url = `${this.apiBaseUrl}api/ReportIssue/DownloadReportIssue/${encodeURIComponent(incidentNo)}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(catchError(this.handleError));
  }

  // 🔹 9. Error handler
  private handleError(error: HttpErrorResponse) {
    console.error('API Error:', error);
    return throwError(() => new Error(error.message || 'Server Error'));
  }
}
